// Legacy: pro-frontend/src/app/resume.service.ts + portfolio.service.ts

import { apiClient } from '@/lib/api/client';

import type {
  PortfolioListItem,
  PortfolioListResponse,
  PortfolioTemplateResponse,
  PortfolioTemplatesListResponse,
  PublicPortfolioExtraInfo,
  PublicPortfolioResponse,
  SavePortfolioTemplateResponse,
} from '../types';
import { resolvePublicPortfolioIdParam } from '../lib/template-id-map';

export async function fetchPortfolios(): Promise<PortfolioListItem[]> {
  const { data } = await apiClient.post<PortfolioListResponse>('/pro/portfolio/list', {});
  if (
    data &&
    (data.status === 'SUCCESS' || data.status === 'success') &&
    Array.isArray(data.portfolios)
  ) {
    return data.portfolios;
  }
  return [];
}

export async function fetchPortfolioTemplate(resumeId: number): Promise<PortfolioTemplateResponse> {
  const { data } = await apiClient.post<PortfolioTemplateResponse>('/pro/portfolio/v2', {
    id: resumeId,
    include_hidden: true,
  });
  return data;
}

export async function fetchPortfolioTemplates(): Promise<PortfolioTemplatesListResponse> {
  const { data } = await apiClient.post<PortfolioTemplatesListResponse>(
    '/pro/portfolio/templates',
    {},
  );
  return data;
}

export async function savePortfolioTemplate(
  resumeId: number,
  templateId: number,
  selectedTheme: string,
  selectedColor: string,
): Promise<SavePortfolioTemplateResponse> {
  const { data } = await apiClient.post<SavePortfolioTemplateResponse>(
    '/pro/portfolio/template/save',
    {
      resume_id: resumeId,
      template_id: templateId,
      selected_theme: selectedTheme,
      selected_color: selectedColor,
    },
  );
  return data;
}

export async function getPublicPortfolio(
  urlId: string,
  extraInfo: PublicPortfolioExtraInfo | null = null,
): Promise<PublicPortfolioResponse> {
  const resolved = resolvePublicPortfolioIdParam(urlId);
  const inputParam =
    resolved.type === 'slug'
      ? { slug: resolved.value, extraInfo }
      : { user_id: resolved.value, extraInfo };

  const viewType = (extraInfo && extraInfo.viewType) || 'portfolio';
  const endpoint =
    viewType === 'resume' ? '/pro/resume/public/v2' : '/pro/portfolio/public/v2';

  const { data } = await apiClient.post<PublicPortfolioResponse>(endpoint, inputParam);
  return data;
}

export async function sendPublicHeartbeat(visitorId: number): Promise<void> {
  await apiClient.post('/pro/resume/public/heartbeat', {
    visitor_id: visitorId,
  });
}

export function buildPublicExtraInfo(viewType: 'portfolio' | 'resume' = 'portfolio'): PublicPortfolioExtraInfo {
  if (typeof window === 'undefined') {
    return { viewType, fullUrl: '' };
  }
  const ua = navigator.userAgent || '';
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
    window.innerWidth < 768;

  return {
    deviceInfo: {
      userAgent: ua,
      platform: navigator.platform || '',
      language: navigator.language || '',
    },
    isMobile,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewType,
    fullUrl: window.location.href,
  };
}
